"use client";

import * as React from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { Typography } from "@/components/ui/typography";
import { Pill } from "@/components/ui/pill";
import { SectionWrapper } from "@/components/ui/section-wrapper";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { SectionHeading } from "@/components/frontend/home/section-heading";

export function DashboardOverview() {
  const t = useTranslations("homeDashboardOverview");
  const sectionRef = useScrollAnimation({ threshold: 0.15 });
  const content = {
    pillLabel: t("pillLabel"),
    title: t("title"),
    description: t("description"),
    imageAlt: t("imageAlt"),
  };

  return (
    <SectionWrapper
      ref={sectionRef.ref}
      variant="default"
      padding="md"
      className="relative"
    >
      <div
        className={`transition-all duration-700 ${
          sectionRef.isVisible
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-6"
        }`}
      >
        {/* Boxed Container */}
        <div className="mx-auto">
          <div
            className={`relative rounded-2xl overflow-hidden shadaw-none dark:shadow-2xl border border-gray-200 dark:border-gray-800 transition-all duration-700 ${
              sectionRef.isVisible
                ? "opacity-100 translate-y-0 scale-100"
                : "opacity-0 translate-y-10 scale-95"
            }`}
            style={{ transitionDelay: "200ms" }}
          >
            {/* Subtle grid background - light effect */}
            <div className="pointer-events-none absolute inset-0 rounded-2xl bg-[radial-gradient(circle_at_center,_rgba(148,163,184,0.08),_transparent_70%)] opacity-60 dark:opacity-30" />

            {/* Top Section - Text with Background Color */}
            <div className="bg-[#fff] dark:bg-[#020203] px-8 pt-16 pb-6 md:pb-[100px]">
              <div className="max-w-4xl mx-auto text-center">
                <SectionHeading
                  pill={content.pillLabel}
                  title={content.title}
                  subtitle={content.description}
                  isVisible={sectionRef.isVisible}
                />
              </div>
            </div>

            {/* Separator Image */}
            <div className="relative w-full xs:h-12 md:h-16 overflow-hidden">
              <Image
                src="/images/home/choiceBg.webp"
                alt="MLM Software Platform - Network Marketing Management System"
                width={1400}
                height={64}
                className="w-full h-[69px] object-cover dark:hidden"
                aria-hidden="true"
                loading="lazy"
                sizes="100vw"
              />
              <Image
                src="/images/home/choiceBgDark.webp"
                alt="Best MLM Software - Multi-Level Marketing Platform for Direct Selling Businesses"
                width={1400}
                height={64}
                className="w-full h-[69px] object-cover hidden dark:block"
                aria-hidden="true"
                loading="lazy"
                sizes="100vw"
              />
            </div>

            {/* Bottom Section - Dashboard Image with Different Background Color */}
            <div className="!bg-[#ebf3ff] dark:!bg-[#0f1e3c] px-3 xs:px-16 pt-1 xs:pt-6 md:pt-[100px] pb-6 xs:pb-16">
              <div className="relative">
                <Image
                  src="/images/best-mlm.webp"
                  alt={content.imageAlt}
                  width={1400}
                  height={900}
                  className="w-full h-auto object-contain mt-6 mt-0 md:mt-[-220px]"
                  priority
                  quality={70}
                  sizes="(max-width: 640px) 100vw, (max-width: 768px) 100vw, (max-width: 1200px) 90vw, 1400px"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
}
